import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Math } from "@/components/Math";
import { Check, X, Lightbulb, Eye, RotateCcw } from "lucide-react";
import { cn } from "@/lib/utils";

interface InteractiveExerciseProps {
  exerciseNumber: number;
  prompt: string;
  answer: string;
  lessonId: string;
  exerciseId: string;
  isCompleted: boolean;
  onComplete: (attempts: number) => void;
}

// Normalize LaTeX for comparison (handles common equivalent forms)
function normalizeLatex(latex: string): string {
  return latex
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/\s*\^\s*/g, '^')
    .replace(/\s*_\s*/g, '_')
    .replace(/\s*\{\s*/g, '{')
    .replace(/\s*\}\s*/g, '}')
    .replace(/x\^{(\d)}/g, 'x^$1')
    .replace(/(\d)\^{(\d)}/g, '$1^$2')
    .toLowerCase();
}

export function InteractiveExercise({
  exerciseNumber,
  prompt,
  answer,
  lessonId,
  exerciseId,
  isCompleted: initialCompleted,
  onComplete,
}: InteractiveExerciseProps) {
  const { t } = useTranslation();
  const [userInput, setUserInput] = useState("");
  const [isCompleted, setIsCompleted] = useState(initialCompleted);
  const [showHint, setShowHint] = useState(false);
  const [showAnswer, setShowAnswer] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [feedback, setFeedback] = useState<'correct' | 'incorrect' | null>(null);

  useEffect(() => {
    setIsCompleted(initialCompleted);
  }, [initialCompleted]);

  const checkAnswer = () => {
    const newAttempts = attempts + 1;
    setAttempts(newAttempts);

    const normalizedUser = normalizeLatex(userInput);
    const normalizedAnswer = normalizeLatex(answer);

    // Check for exact match or common equivalents
    const isCorrect = normalizedUser === normalizedAnswer || 
      // Handle space variations
      userInput.replace(/\s/g, '') === answer.replace(/\s/g, '');

    if (isCorrect) {
      setFeedback('correct');
      setIsCompleted(true);
      onComplete(newAttempts);
    } else {
      setFeedback('incorrect');
      setTimeout(() => setFeedback(null), 2000);
    }
  };

  const reset = () => {
    setUserInput("");
    setShowHint(false);
    setShowAnswer(false);
    setFeedback(null);
    setAttempts(0);
  };

  const getHint = () => {
    // Generate a hint based on the answer
    const hintLength = globalThis.Math.min(answer.length / 3, 10);
    const firstFew = answer.slice(0, hintLength);
    return `${t('latexCenter.exercises.hintStart', 'Start with:')} ${firstFew}...`;
  };

  return (
    <Card className={cn(
      "p-4 transition-all",
      isCompleted && "border-green-500/50 bg-green-500/5",
      feedback === 'incorrect' && "border-red-500/50 animate-shake"
    )}>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-start justify-between gap-2">
          <p className="font-medium">
            {t('latexCenter.exercises.exerciseLabel', 'Exercise')} {exerciseNumber}: {prompt}
          </p>
          {isCompleted && (
            <div className="flex items-center gap-1 text-green-600 shrink-0">
              <Check className="h-5 w-5" />
              <span className="text-sm font-medium">{t('latexCenter.exercises.completed', 'Completed')}</span>
            </div>
          )}
        </div>

        {/* Input Area */}
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-sm text-muted-foreground">
              {t('latexCenter.exercises.yourAnswer', 'Your LaTeX:')}
            </label>
            <Textarea
              value={userInput}
              onChange={(e) => setUserInput(e.target.value)}
              placeholder={t('latexCenter.exercises.placeholder', 'Type your LaTeX here...')}
              className="font-mono text-sm min-h-[80px]"
              disabled={isCompleted}
            />
          </div>
          
          {/* Preview */}
          <div className="space-y-2">
            <label className="text-sm text-muted-foreground">
              {t('latexCenter.exercises.preview', 'Preview:')}
            </label>
            <div className="min-h-[80px] p-3 rounded-md bg-slate-100 dark:bg-slate-200 flex items-center justify-center">
              {userInput ? (
                <Math block>{userInput}</Math>
              ) : (
                <span className="text-muted-foreground text-sm">
                  {t('latexCenter.exercises.previewPlaceholder', 'Your equation will appear here')}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Hint */}
        {showHint && !isCompleted && (
          <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-md text-sm">
            <p className="font-medium text-amber-700 dark:text-amber-400 flex items-center gap-2">
              <Lightbulb className="h-4 w-4" />
              {t('latexCenter.exercises.hint', 'Hint')}
            </p>
            <p className="mt-1 font-mono text-xs">{getHint()}</p>
          </div>
        )}

        {/* Show Answer */}
        {showAnswer && (
          <div className="p-3 bg-muted/50 rounded-md">
            <p className="text-sm text-muted-foreground mb-2">{t('latexCenter.exercises.correctAnswer', 'Correct answer:')}</p>
            <code className="text-sm font-mono block mb-2">{answer}</code>
            <div className="p-2 bg-slate-100 dark:bg-slate-200 rounded inline-block">
              <Math>{answer}</Math>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-wrap gap-2">
          {!isCompleted && (
            <>
              <Button 
                onClick={checkAnswer} 
                disabled={!userInput.trim()}
                className={cn(
                  feedback === 'correct' && "bg-green-600 hover:bg-green-700"
                )}
              >
                {feedback === 'correct' ? (
                  <><Check className="h-4 w-4 mr-1" /> {t('latexCenter.exercises.correct', 'Correct!')}</>
                ) : feedback === 'incorrect' ? (
                  <><X className="h-4 w-4 mr-1" /> {t('latexCenter.exercises.tryAgain', 'Try Again')}</>
                ) : (
                  t('latexCenter.exercises.checkAnswer', 'Check Answer')
                )}
              </Button>
              
              {!showHint && attempts >= 1 && (
                <Button variant="outline" onClick={() => setShowHint(true)}>
                  <Lightbulb className="h-4 w-4 mr-1" />
                  {t('latexCenter.exercises.showHint', 'Show Hint')}
                </Button>
              )}
              
              {!showAnswer && attempts >= 3 && (
                <Button variant="outline" onClick={() => setShowAnswer(true)}>
                  <Eye className="h-4 w-4 mr-1" />
                  {t('latexCenter.exercises.showSolution', 'Show Solution')}
                </Button>
              )}
            </>
          )}
          
          {isCompleted && (
            <Button variant="outline" size="sm" onClick={reset}>
              <RotateCcw className="h-4 w-4 mr-1" />
              {t('latexCenter.exercises.practiceAgain', 'Practice Again')}
            </Button>
          )}
        </div>

        {/* Attempt Counter */}
        {attempts > 0 && !isCompleted && (
          <p className="text-xs text-muted-foreground">
            {t('latexCenter.exercises.attempts', 'Attempts:')} {attempts}
          </p>
        )}
      </div>
    </Card>
  );
}
